import {Board} from '@github-ui/board'
import {BoardColumnHeader, BoardRowHeader, BoardSkeleton} from '@github-ui/board/frames'
import type {BoardColumn, BoardData, BoardLabels, BoardRow} from '@github-ui/board-core'
import {Blankslate} from '@primer/react/experimental'
import type {ReactNode} from 'react'

import styles from './BoardSurface.module.css'
import type {SavedViewsBoardData, SavedViewsBoardItem} from '../types'
import {useBoardViewportHeight} from './use-board-viewport-height'

/** A swimlane's depth. Rows are a fixed height, sized by this rather than by their cards. */
const SWIMLANE_ROW_HEIGHT = '24rem'

/** A column's name and count, which is all a host with nothing richer to say needs. */
const renderPlainColumnHeader = (column: BoardColumn) => <BoardColumnHeader column={column} />

/** The same for a swimlane. */
const renderPlainRowHeader = (row: BoardRow) => <BoardRowHeader row={row} />

export type BoardSurfaceSkeletonProps = {
  labels: BoardLabels
  /**
   * The shape of the board being replaced, so the swap costs the reader no movement.
   *
   * Omit both on a layout change, where nothing on screen is a board and there is no
   * shape to copy. The placeholder draws its own defaults.
   */
  columns?: number
  /** Omit on a board with no swimlane, which draws one band and no row header. */
  rows?: number
}

/**
 * A placeholder board in the box the board fills.
 *
 * The box is the board's own requirement rather than the placeholder's: the board owns
 * its scroll containers and takes its height from here, so a placeholder drawn outside
 * this box would move the page on the way in and again on the way out.
 */
export function BoardSurfaceSkeleton({labels, columns, rows}: BoardSurfaceSkeletonProps) {
  const surfaceRef = useBoardViewportHeight()

  // Carries no `board-view` test id, which names the board being drawn. A placeholder
  // that answered to it would let a test waiting for a board settle on the thing
  // standing in for one.
  return (
    <div ref={surfaceRef} className={styles.boardSurface}>
      <BoardSkeleton
        labels={labels}
        columns={columns}
        rows={rows}
        rowHeight={rows === undefined ? undefined : SWIMLANE_ROW_HEIGHT}
      />
    </div>
  )
}

export type BoardSurfaceProps<TItem extends SavedViewsBoardItem> = {
  boardData: SavedViewsBoardData<TItem>
  renderCard: (item: TItem) => ReactNode
  /**
   * How a column names itself. Optional, because a column's name is the one thing this
   * layer can draw without knowing the domain, and a host that has richer material for
   * the field it grouped by -- a label's colour, a milestone's due date -- draws that
   * instead.
   */
  renderColumnHeader?: (column: BoardColumn) => ReactNode
  /**
   * The same for a swimlane. Ignored on a board with no swimlane, which has no row for a
   * reader to see: its single row is the board's own, not a bucket of a field.
   */
  renderRowHeader?: (row: BoardRow) => ReactNode
  /**
   * What is drawn instead of the grid when the response filled no column. Optional, because
   * why a grouped board came back empty is a statement about the search, which this layer is
   * handed nothing about. A host that supplies nothing gets the empty grid.
   */
  renderEmptyState?: () => ReactNode
  /**
   * The board's own data is being replaced, so everything drawn is about to be wrong.
   *
   * Stated by the host rather than read off the data, because what is in flight is the
   * host's request: a grouping change rebuilds both axes and a sort reorders every cell,
   * and neither is visible from the board data still on screen. The same fact
   * `IssuesListSurface` is handed for the list layout.
   */
  isLoading?: boolean
  labels: BoardLabels
}

/**
 * Nothing is coming for this axis: no column arrived, none is on its way, and the server
 * did not stop short of one.
 *
 * `hasMoreColumns` and `isLoadingColumns` are a board mid-enumeration, and `columnsTruncated`
 * is `BoardColumnPaging` drawing the notice to narrow the search -- each of them says
 * something about an axis that is not yet, or never will be, complete, and an empty state
 * drawn over any of them replaces a true statement with a wrong one.
 */
function hasNoColumns<TItem>(data: BoardData<TItem>): boolean {
  return (
    data.columns.length === 0 &&
    !data.hasMoreColumns &&
    data.isLoadingColumns !== true &&
    data.columnsTruncated !== true
  )
}

/**
 * The generic glue over `@github-ui/board`: it wires the mapped data and is agnostic about the
 * domain except for the `renderCard` and `renderColumnHeader` it is handed. A different host
 * reuses this unchanged and supplies its own board data, card and header.
 *
 * The board owns its own scroll containers and cannot give itself a height: it fills its parent
 * and windows against that parent's height, so an unbounded parent makes it stretch the page and
 * its scrolling, virtualization and paging break together and silently. The host is sized to the
 * viewport at runtime (see `useBoardViewportHeight`) because the page layout above it renders a
 * header and sidebar and so cannot hand the board a bounded height in CSS alone.
 */
export function BoardSurface<TItem extends SavedViewsBoardItem>({
  boardData,
  renderCard,
  renderColumnHeader,
  renderRowHeader,
  renderEmptyState,
  isLoading = false,
  labels,
}: BoardSurfaceProps<TItem>) {
  const {data, columnField, rowField} = boardData
  const surfaceRef = useBoardViewportHeight()
  const swimlaned = rowField != null

  // Before the two states below, which both describe the board being replaced rather than
  // the one arriving: a grouping change is exactly the case where the old `columnField`
  // and the old columns are about to be something else.
  if (isLoading) {
    return (
      <BoardSurfaceSkeleton
        labels={labels}
        columns={data.columns.length}
        rows={swimlaned ? data.rows.length : undefined}
      />
    )
  }

  // D29: a board with no grouping has nothing to arrange into columns. That is an empty
  // state, not a fabricated single column.
  if (columnField == null) {
    return (
      <Blankslate>
        <Blankslate.Heading>Nothing to group by</Blankslate.Heading>
        <Blankslate.Description>Choose a field to group by to see this view as a board.</Blankslate.Description>
      </Blankslate>
    )
  }

  if (renderEmptyState && hasNoColumns(data)) return renderEmptyState()

  return (
    <div ref={surfaceRef} className={styles.boardSurface} data-testid="board-view">
      <Board<TItem>
        data={data}
        getItemId={item => item.globalId}
        getItemLabel={item => item.title}
        labels={labels}
        renderCard={renderCard}
        renderColumnHeader={renderColumnHeader ?? renderPlainColumnHeader}
        renderRowHeader={swimlaned ? (renderRowHeader ?? renderPlainRowHeader) : undefined}
        rowHeight={swimlaned ? SWIMLANE_ROW_HEIGHT : undefined}
      />
    </div>
  )
}
